import { motion } from 'framer-motion';
import { 
  Play, 
  Gift, 
  Star, 
  Sparkles,
  Zap,
  TrendingUp,
  Clock
} from 'lucide-react';

import { showOnclickaRewardedPopup } from '../hooks';
import { showCombinedRewardedAds, showCombinedInterstitialAds } from '../hooks/useCombinedAds';
import { useAppStore } from '../store/useAppStore';
import { useConfigStore } from '../store/useConfigStore';
import { useTelegram } from '../hooks';
import { adsApi } from '../api';
import toast from 'react-hot-toast';
import { useState, useMemo, useEffect, useCallback } from 'react';

interface AdCooldown {
  adType: string;
  network: string;
  remainingSeconds: number;
  totalSeconds: number;
}

export function OffersPage() {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [cooldowns, setCooldowns] = useState<AdCooldown[]>([]);
  const [globalCooldown, setGlobalCooldown] = useState<number>(0);
  const updateBalance = useAppStore((state) => state.updateBalance);
  const { hapticFeedback, hapticNotification } = useTelegram();
  
  // Get reward amounts from config store
  const adsgram = useConfigStore((state) => state.adsgram);
  const onclicka = useConfigStore((state) => state.onclicka);

  // Fetch cooldowns from backend
  const fetchCooldowns = useCallback(async () => {
    try {
      const response = await adsApi.getAdCooldowns();
      setCooldowns(response.cooldowns);
      setGlobalCooldown(response.globalCooldown);
    } catch (error) {
      console.error('Failed to fetch cooldowns:', error);
    }
  }, []);

  // Fetch cooldowns on mount
  useEffect(() => {
    fetchCooldowns();
    // Refresh cooldowns every 5 seconds
    const interval = setInterval(fetchCooldowns, 5000);
    return () => clearInterval(interval);
  }, [fetchCooldowns]);

  // Countdown timer for local UI updates
  useEffect(() => {
    if (cooldowns.some(c => c.remainingSeconds > 0) || globalCooldown > 0) {
      const timer = setInterval(() => {
        setCooldowns(prev => prev.map(c => ({
          ...c,
          remainingSeconds: Math.max(0, c.remainingSeconds - 1)
        })));
        setGlobalCooldown(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldowns, globalCooldown]);

  // Get cooldown for a specific action
  const getCooldownForAction = (action: string): number => {
    // Check global cooldown first
    if (globalCooldown > 0) return globalCooldown;
    
    // Map action to ad types
    const actionToAdTypes: Record<string, string[]> = {
      'rewarded': ['rewarded', 'watch_video'],
      'premium': ['premium', 'interstitial'],
      'interstitial': ['interstitial', 'quick_view'],
      'popup': ['popup', 'sponsor_page'],
    };
    
    const adTypes = actionToAdTypes[action] || [action];
    const matchingCooldowns = cooldowns.filter(c => adTypes.includes(c.adType));
    if (matchingCooldowns.length === 0) return 0;
    
    return Math.max(...matchingCooldowns.map(c => c.remainingSeconds));
  };

  // Format seconds to mm:ss
  const formatCooldown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  // Quick reward options - combined ads with original rewards
  // Fallback values MUST match DEFAULT_CONFIG in backend/src/models/SystemConfig.ts
  const QUICK_REWARDS = useMemo(() => [
    {
      id: 'video1',
      title: 'Watch Video',
      description: 'Quick 15-30 second video',
      icon: Play,
      reward: adsgram.rewardAmount ?? 20,
      color: 'from-orange-500 to-red-500',
      action: 'combined_rewarded'
    },
    {
      id: 'video2',
      title: 'Premium Video',
      description: 'Higher reward video',
      icon: Star,
      reward: onclicka.rewardAmount ?? 20,
      color: 'from-purple-500 to-pink-500',
      action: 'combined_interstitial'
    },
    {
      id: 'instant',
      title: 'Instant Reward',
      description: 'View sponsor content',
      icon: Zap,
      reward: adsgram.interstitialReward ?? 15,
      color: 'from-blue-500 to-cyan-500',
      action: 'combined_interstitial'
    },
    {
      id: 'bonus',
      title: 'Bonus Offer',
      description: 'Special offer page',
      icon: Gift,
      reward: onclicka.rewardAmount ?? 20,
      color: 'from-green-500 to-emerald-500',
      action: 'popup'
    },
  ], [adsgram, onclicka]);

  const handleQuickReward = async (item: typeof QUICK_REWARDS[0]) => {
    setLoadingId(item.id);
    hapticFeedback('medium');
    
    let sessionToken: string | null = null;
    
    try {
      let success = false;
      let adNetwork = 'combined';
      
      // Step 1: Start ad session on server BEFORE showing ad
      try {
        const sessionResult = await adsApi.startAdSession(item.id, item.action);
        if (!sessionResult.success || !sessionResult.sessionToken) {
          throw new Error('Failed to start ad session');
        }
        sessionToken = sessionResult.sessionToken;
      } catch (sessionError: any) {
        console.error('Failed to start ad session:', sessionError);
        toast.error(sessionError.response?.data?.error || 'Failed to start. Please try again.');
        setLoadingId(null);
        return;
      }
      
      // Step 2: Show the actual ad
      switch (item.action) {
        case 'combined_rewarded': {
          // Show combined ads: AdsGram rewarded first, then Onclicka
          toast.loading('Showing ads...', { id: 'quick-reward-ads' });
          const result = await showCombinedRewardedAds('offers_page');
          toast.dismiss('quick-reward-ads');
          success = result.success;
          if (result.adsgramCompleted && result.onclickaCompleted) {
            adNetwork = 'combined';
          } else if (result.adsgramCompleted) {
            adNetwork = 'adsgram';
          } else if (result.onclickaCompleted) {
            adNetwork = 'onclicka';
          }
          break;
        }
        case 'combined_interstitial': {
          // Show combined ads: AdsGram interstitial first, then Onclicka
          toast.loading('Showing ads...', { id: 'quick-reward-ads' });
          const result = await showCombinedInterstitialAds('offers_page');
          toast.dismiss('quick-reward-ads');
          success = result.success;
          if (result.adsgramCompleted && result.onclickaCompleted) {
            adNetwork = 'combined';
          } else if (result.adsgramCompleted) {
            adNetwork = 'adsgram';
          } else if (result.onclickaCompleted) {
            adNetwork = 'onclicka';
          }
          break;
        }
        case 'popup':
          success = await showOnclickaRewardedPopup('offers_page');
          adNetwork = 'onclicka';
          break;
      }
      
      if (success && sessionToken) {
        hapticNotification('success');
        
        // Record ad view on server with session token - SERVER determines the reward amount
        try {
          const result = await adsApi.recordAdView(adNetwork, item.action, sessionToken);
          if (result.newBalance !== undefined) {
            updateBalance(result.newBalance);
          }
          // Show server-calculated reward, fallback to expected amount for UI
          const earnedReward = result.reward ?? item.reward;
          toast.success(`+${earnedReward} GEMI earned!`);
        } catch (e: any) {
          console.error('Failed to record ad view:', e);
          // Show error but don't give fake reward
          if (e.response?.status === 429) {
            toast.error(e.response?.data?.error || 'Please wait before watching another ad');
          } else {
            toast.error('Failed to record reward. Please try again.');
          }
        }
        
        // Refresh cooldowns after successful ad
        fetchCooldowns();
      }
    } catch (error) {
      console.error('Quick reward error:', error);
      toast.error('Failed to load. Try again!');
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Quick Rewards Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-4"
      >
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          <h3 className="font-bold">Quick Rewards</h3>
          <span className="ml-auto text-xs text-white/50 bg-white/10 px-2 py-1 rounded-full">Instant</span>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          {QUICK_REWARDS.map((item) => {
            const cooldownSeconds = getCooldownForAction(item.action);
            const isOnCooldown = cooldownSeconds > 0;
            
            return (
              <button
                key={item.id}
                onClick={() => !isOnCooldown && handleQuickReward(item)}
                disabled={loadingId !== null || isOnCooldown}
                className={`relative p-4 rounded-xl bg-gradient-to-br ${item.color} text-white text-left transition-transform ${
                  isOnCooldown 
                    ? 'opacity-60 cursor-not-allowed' 
                    : 'active:scale-95 disabled:opacity-50'
                }`}
              >
                {isOnCooldown ? (
                  <Clock className="w-6 h-6 mb-2 opacity-70" />
                ) : (
                  <item.icon className="w-6 h-6 mb-2" />
                )}
                <p className="font-semibold text-sm">{item.title}</p>
                <p className="text-xs text-white/80">
                  {isOnCooldown 
                    ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatCooldown(cooldownSeconds)}</span>
                    : item.description}
                </p>
                <div className={`mt-2 text-xs font-bold rounded-full px-2 py-1 inline-block ${
                  isOnCooldown ? 'bg-black/30' : 'bg-white/20'
                }`}>
                  {isOnCooldown 
                    ? `Wait ${formatCooldown(cooldownSeconds)}`
                    : `+${item.reward} GEMI`}
                </div>
                {loadingId === item.id && (
                  <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Tips */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20"
      >
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1 text-sm">Maximize Earnings</p>
            <ul className="text-white/60 text-xs space-y-1">
              <li>• Complete offers for the highest rewards</li>
              <li>• Watch all available videos daily</li>
              <li>• Check back often for new tasks</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
